import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import { TableRow, TableCell } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ChartButton from "../../../components/Button/ChartButton";
import DateRangePicker from "../../../components/FormFilelds/DateRangePicker";
import { BASE_URL, getAxiosConfig } from "../../../components/interface/Config";

import TableComponent from "../../../components/Table/TableComponent";
import TablePaginationComponent from "../../../components/TablePaginationComponent";
import { format } from "date-fns";
import { ObjectiveResponse, Spo2 } from "../../../components/interface/ObjectiveInterface";

const Spo2Form: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [spO2, setSpO2] = useState<Spo2[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openSpo2Dialog, setOpenSpo2Dialog] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "info" | "warning" | "error"
  >("success");
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });
  const showSnackbar = (
    message: string,
    severity: "success" | "info" | "warning" | "error"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchSpO2 = useCallback(
    async (startDate: string = "", endDate: string = "") => {
      const formattedStartDate = startDate
        ? new Date(startDate).toISOString().split("T")[0]
        : "";
      const formattedEndDate = endDate
        ? new Date(endDate).toISOString().split("T")[0]
        : "";
      setLoading(true);
      try {
        console.log("Fetching data with parameters:", {
          limit: rowsPerPage,
          page: page + 1,
          from: formattedStartDate,
          to: formattedEndDate,
          order_by: "details->date",
          dir: 2,
          user_id: patientId,
          slug: "spO2",
          searchkey: searchTerm || undefined,
        });

        const response = await axios.get<ObjectiveResponse>(
          `${BASE_URL}/resource/vitals`,
          {
            params: {
              limit: rowsPerPage,
              page: page + 1,
              from: formattedStartDate,
              to: formattedEndDate,
              order_by: "details->date",
              dir: 2,
              user_id: patientId,
              slug: "spO2",
              searchkey: searchTerm || undefined,
            },
            headers: getAxiosConfig().headers,
          }
        );

        const vitalsData = response.data.data.vitals;
        const total = response.data.data.pagination?.total;

        if (Array.isArray(vitalsData) && vitalsData.length > 0) {
          const formattedData: Spo2[] = vitalsData.map((vital) => ({
            id: vital.id,
            user_id: vital.user_id,
            consult_id: vital.consult_id,
            peripheral_id: vital.peripheral_id,
            slug: vital.slug,
            details: {
              date: format(new Date(vital.details.date), 'dd-MM-yyyy'),
              spo2: vital.details.spo2,
              time: vital.details.time,
              unit: vital.details.unit,
              spo2Flag: vital.details.spo2Flag,
              range_code: vital.details.range_code,
              spo2FlagColor: vital.details.spo2FlagColor,
              pulse: vital.details.pulse,
            },
            freeze: vital.freeze,
            document: vital.document,
          }));

          setSpO2(formattedData);
        } else {
          setSpO2([]); // Clear existing data if no vitals are returned
          console.log("No SpO2 data available for the given date range.");
        }

        if (total) {
          setTotalRecords(total);
        }
      } catch (error) {
        console.error("Error fetching SpO2:", error);
        showSnackbar("Error fetching data", "error");
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchTerm, patientId]
  );

  useEffect(() => {
    fetchSpO2(dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage, searchTerm]);


  const handleClearFilters = () => {
    setSearchTerm("");
    setDateRange({ startDate: "", endDate: "" });
    setPage(0); // Reset to the first page
    setRowsPerPage(5); // Reset rows per page if needed
    fetchSpO2(); // Fetch all records again
    setFiltersApplied(false); // Hide the clear button
  };

  const renderRow = (item: Spo2, index: number) => (
    <TableRow key={index}>
      <TableCell
        className="px-4 py-3 font-jost"
        sx={{ fontFamily: "Jost, sans-serif", fontSize: "1rem" }}
      >
        {" "}
        {page * rowsPerPage + index + 1}
      </TableCell>
      <TableCell sx={{ fontFamily: "Jost, sans-serif", fontSize: "1rem" }}>
        {item.details!.date}
      </TableCell>
      <TableCell sx={{ fontFamily: "Jost, sans-serif", fontSize: "1rem" }}>
        {item.details!.time}
      </TableCell>
      <TableCell sx={{ fontFamily: "Jost, sans-serif", fontSize: "1rem" }}>
        {item.details!.spo2!}
        {item.details!.unit}
      </TableCell>
      <TableCell sx={{ fontFamily: "Jost, sans-serif", fontSize: "1rem" }}>
        {item.details?.pulse}
      </TableCell>

      <TableCell sx={{ fontFamily: "Jost, sans-serif", fontSize: "1rem" }}>
        <span
          style={{
            backgroundColor: item.details!.range_code,
            padding: "4px 8px",
            borderRadius: "8px",
            color: "#F9F9F9",
          }}
        >
          {item.details!.spo2Flag}
        </span>
      </TableCell>
    </TableRow>
  );

  const chartData = {
    labels: spO2
      .map((record) => record.details!.date) // Get the dates
      .filter((date): date is string => date !== undefined), // Filter out undefined dates
    datasets: [
      {
        label: "SpO2 (%)",
        data: spO2
          .map((record) => record.details!.spo2) // Get the SpO2 values
          .filter((spo2): spo2 is number => spo2 !== undefined), // Filter out undefined SpO2 values
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw}`,
        },
      },
    },
  };

  return (
    <div>
      <div className="flex items-end justify-between w-full gap-12">
        <div className="flex flex-grow space-x-2 gap-2">
          <DateRangePicker
            startDateId={"Spo2_Start_Date"}
            endDateId={"Spo2_End_Date"}
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
              setFiltersApplied(true);
            }}
            value={dateRange} // Pass the selected date range
          />
          <IconButton
            icon={<SearchIcon />}
            onClick={() => {
              fetchSpO2(dateRange.startDate, dateRange.endDate);
              setFiltersApplied(true); // Mark filters as applied when search is clicked
            }}
          />

          {filtersApplied && (
            <IconButton icon={<ClearIcon />} onClick={handleClearFilters} />
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <ChartButton
            text={showChart ? "List" : "Chart"}
            onClick={() => setShowChart((prev) => !prev)}
          />
        </div>
      </div>
      {loading ? (
        <div className="text-center my-4"></div>
      ) : (
        <>
          {showChart ? (
            <Chart data={chartData} options={chartOptions} />
          ) : (
            <div>
              <TableComponent
                headers={["S.No", "Date", "Time", "SpO2", "Pulse", "Result"]}
                data={spO2}
                loading={loading}
                renderRow={renderRow}
                error={"error"}
              />
            </div>
          )}
        </>
      )}

      <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
    </div>
  );
};

export default Spo2Form;
